import { useMemo, useState, type FormEvent } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router';
import {
  DEFAULT_THEME,
  STARTER_TEMPLATES,
  renderPage,
  type Block,
  type RenderContext,
  type StarterTemplate,
} from '@poof/templates';
import { pages as pagesApi, ApiError } from '@/lib/api';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button, Surface } from '@/components/ui';

/**
 * Template picker — every option shows a real, live mobile-frame preview
 * rendered with the exact same `renderPage()` the bouncer uses. Customers
 * see what they'll get before they click — no abstract icons or vague
 * descriptions to interpret.
 *
 * Each template's `previewBlocks` (sample-populated variant) drives the
 * preview; `buildBlocks` is what we actually create at submit time so the
 * customer's new page starts with placeholders, not sample copy.
 */
export default function NewPage() {
  const [name, setName] = useState('');
  // Default to "blank" so the page lands neutral; customers actively
  // opt into a styled template rather than getting nudged into one we
  // picked for them. Lets the gallery breathe.
  const [templateId, setTemplateId] = useState<string>('blank');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const navigate = useNavigate();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await pagesApi.create({ name: name.trim(), templateId });
      navigate(`/app/pages/${res.id}`);
    } catch (e) {
      setErr(e instanceof ApiError ? errorFor(e.code) : (e as Error).message);
      setBusy(false);
    }
  }

  return (
    <DashboardLayout title="Create a new page" wide>
      <RouterLink
        to="/app/pages"
        className="text-sm text-[var(--color-on-surface-variant)] transition hover:text-[var(--color-foreground)]"
      >
        ← Back to pages
      </RouterLink>

      <p className="mt-4 text-[var(--color-on-surface-variant)]">
        Pick a starting point — what you see in the preview is what you&apos;ll
        get. Every block is editable from there.
      </p>

      <form onSubmit={onSubmit} className="mt-7 space-y-6">
        {/* ---------- ABOVE THE FOLD: page name + Create button ---------- */}
        <Surface tone="raised" className="p-5 sm:p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <label className="block min-w-0 flex-1">
              <span className="text-xs font-semibold uppercase tracking-[0.06em] text-[var(--color-on-surface-variant)]">
                Page name
              </span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="My Page"
                required
                maxLength={120}
                autoFocus
                className="mt-1.5 block w-full rounded-xl border border-[var(--color-border)] bg-[var(--surface-deep)] px-4 py-2.5 outline-none transition focus:border-[var(--color-ring)]"
              />
            </label>
            <div className="flex items-center gap-3 sm:pb-0.5">
              <Button type="submit" disabled={busy || !name.trim()}>
                {busy ? 'Creating…' : 'Create page'}
              </Button>
              <RouterLink
                to="/app/pages"
                className="text-sm text-[var(--color-on-surface-variant)] transition hover:text-[var(--color-foreground)]"
              >
                Cancel
              </RouterLink>
            </div>
          </div>
          <div className="mt-3 flex items-center justify-between gap-3 text-xs text-[var(--color-on-surface-variant)]">
            <span>
              Using template:{' '}
              <strong className="text-[var(--color-foreground)]">
                {STARTER_TEMPLATES.find((t) => t.id === templateId)?.icon}{' '}
                {STARTER_TEMPLATES.find((t) => t.id === templateId)?.name}
              </strong>{' '}
              — change below.
            </span>
            {err && <span className="text-[var(--color-destructive)]">{err}</span>}
          </div>
        </Surface>

        {/* ---------- Template gallery (real live previews) ---------- */}
        <fieldset>
          <legend className="mb-4 text-xs font-semibold uppercase tracking-[0.06em] text-[var(--color-on-surface-variant)]">
            Pick a template
          </legend>
          <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {STARTER_TEMPLATES.map((t) => (
              <TemplateCard
                key={t.id}
                template={t}
                selected={templateId === t.id}
                onSelect={() => setTemplateId(t.id)}
              />
            ))}
          </div>
        </fieldset>
      </form>
    </DashboardLayout>
  );
}

/**
 * One template card — mobile-frame iframe preview on top, name + description
 * below. Renders the template's `previewBlocks` (sample-populated) via the
 * same `renderPage()` the bouncer uses, so what you see is precisely the
 * shape, layout, theme, and feel of the real page.
 */
function TemplateCard({
  template,
  selected,
  onSelect,
}: {
  template: StarterTemplate;
  selected: boolean;
  onSelect: () => void;
}) {
  const previewHtml = useMemo(() => {
    const builder = template.previewBlocks ?? template.buildBlocks;
    const mkId = () => crypto.randomUUID();
    const blocks = builder(mkId) as unknown as Block[];
    const theme = {
      ...DEFAULT_THEME,
      ...(template.theme || {}),
      ...(template.previewTheme || {}),
    };
    // tracking off so preview iframes don't beacon synthetic clicks.
    const ctx: RenderContext = { showBranding: false, plan: 'free', tracking: false };
    return renderPage({ blocks, theme, meta: {}, name: template.name }, ctx);
  }, [template]);

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={`group flex flex-col gap-3 rounded-2xl border p-3 text-left transition ${
        selected
          ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/8 shadow-[var(--glow-primary)]'
          : 'border-[var(--color-border)] glass hover:border-[var(--color-primary)]/40'
      }`}
    >
      {/* Mobile frame — narrow rounded device shell containing a scaled
          iframe so the preview reads at thumbnail size while the inner
          rendering uses real mobile-width CSS for correct proportions. */}
      <div
        className={`relative mx-auto w-full overflow-hidden rounded-[28px] border-[6px] border-[#0a0a0a] shadow-xl transition ${
          selected ? 'shadow-[var(--color-primary)]/20' : ''
        }`}
        style={{ aspectRatio: '9 / 16' }}
      >
        {/* Pointer-events: none on the iframe stops clicks from
            navigating into the preview — the entire card is the
            click target. */}
        <iframe
          title={`${template.name} preview`}
          srcDoc={previewHtml}
          sandbox="allow-same-origin allow-scripts"
          loading="lazy"
          className="pointer-events-none block h-full w-full border-0 bg-white"
          // Scale a 380px-wide page into the thumbnail. The exact scale
          // depends on the column width; we use viewport-relative
          // sizing inside the iframe instead — see the wrapper width below.
          style={{
            transform: 'scale(0.5)',
            transformOrigin: 'top left',
            width: '200%',
            height: '200%',
          }}
        />

        {/* Selected check overlay */}
        {selected && (
          <div className="pointer-events-none absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-[var(--color-primary)] text-[var(--color-primary-foreground)] shadow-lg">
            <svg viewBox="0 0 20 20" width="14" height="14" fill="currentColor" aria-hidden>
              <path d="M16.7 5.3a1 1 0 010 1.4l-7 7a1 1 0 01-1.4 0l-4-4a1 1 0 011.4-1.4L9 11.6l6.3-6.3a1 1 0 011.4 0z" />
            </svg>
          </div>
        )}
      </div>

      <div>
        <div className="flex items-center gap-1.5">
          <span className="text-base">{template.icon}</span>
          <span className="font-medium">{template.name}</span>
        </div>
        <p className="mt-0.5 text-xs leading-snug text-[var(--color-on-surface-variant)]">
          {template.description}
        </p>
      </div>
    </button>
  );
}

function errorFor(code: string): string {
  switch (code) {
    case 'name_required':
      return 'Give the page a name.';
    case 'name_too_long':
      return 'Name too long — keep it under 120 characters.';
    case 'template_not_found':
      return 'That template doesn’t exist anymore. Pick another.';
    case 'not_authorized':
      return 'You don’t have permission to create pages here.';
    default:
      return code;
  }
}
